/**
 * Hook for fetching learning assignments for the current user
 * Fetches both curriculum and course assignments that are incomplete
 */

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";

export interface UserAssignment {
  id: string;
  type: 'curriculum' | 'course';
  item_id: string;
  item_title: string;
  item_description: string | null;
  item_image_url: string | null;
  assigned_by_name: string;
  assigned_at: string;
  due_date: string | null;
  required: boolean;
  notes: string | null;
  acknowledged_at: string | null;
}

interface CurriculumAssignmentRow {
  id: string;
  curriculum_id: string;
  user_id: string;
  assigned_by: string;
  assigned_at: string;
  acknowledged_at: string | null;
  due_date: string | null;
  required: boolean;
  notes: string | null;
  completed_at: string | null;
  curriculum: {
    id: string;
    title: string;
    description: string | null;
    thumbnail_url: string | null;
  } | null;
  assigned_by_profile: {
    full_name: string;
  } | null;
}

interface CourseAssignmentRow {
  id: string;
  course_id: string;
  user_id: string;
  assigned_by: string;
  assigned_at: string;
  acknowledged_at: string | null;
  due_date: string | null;
  required: boolean;
  notes: string | null;
  completed_at: string | null;
  course: {
    id: string;
    title: string;
    description: string | null;
    thumbnail_url: string | null;
  } | null;
  assigned_by_profile: {
    full_name: string;
  } | null;
}

function sortAssignments(assignments: UserAssignment[]): UserAssignment[] {
  return assignments.sort((a, b) => {
    // 1. Required assignments first
    if (a.required !== b.required) {
      return a.required ? -1 : 1;
    }

    // 2. By due date (soonest first, null dates last)
    if (a.due_date !== b.due_date) {
      if (!a.due_date) return 1;
      if (!b.due_date) return -1;
      return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    }

    // 3. By assigned date (newest first)
    return new Date(b.assigned_at).getTime() - new Date(a.assigned_at).getTime();
  });
}

async function fetchUserAssignments(userId: string): Promise<UserAssignment[]> {
  // Fetch curriculum assignments
  const { data: curriculaAssignments, error: curriculaError } = await supabase
    .from('curriculum_assignments')
    .select(`
      id,
      curriculum_id,
      user_id,
      assigned_by,
      assigned_at,
      acknowledged_at,
      due_date,
      required,
      notes,
      completed_at,
      curriculum:curricula(id, title, description, thumbnail_url),
      assigned_by_profile:profiles(full_name)
    `)
    .eq('user_id', userId)
    .is('completed_at', null);

  if (curriculaError) {
    console.error('[useUserAssignments] Error fetching curriculum assignments:', curriculaError);
    throw curriculaError;
  }

  // Fetch course assignments
  const { data: courseAssignments, error: courseError } = await supabase
    .from('course_assignments')
    .select(`
      id,
      course_id,
      user_id,
      assigned_by,
      assigned_at,
      acknowledged_at,
      due_date,
      required,
      notes,
      completed_at,
      course:courses(id, title, description, thumbnail_url),
      assigned_by_profile:profiles(full_name)
    `)
    .eq('user_id', userId)
    .is('completed_at', null);

  if (courseError) {
    console.error('[useUserAssignments] Error fetching course assignments:', courseError);
    throw courseError;
  }

  // Transform curriculum assignments
  const transformedCurricula: UserAssignment[] = (curriculaAssignments as unknown as CurriculumAssignmentRow[] || [])
    .filter(a => a.curriculum)
    .map(a => ({
      id: a.id,
      type: 'curriculum' as const,
      item_id: a.curriculum_id,
      item_title: a.curriculum?.title || 'Unknown Curriculum',
      item_description: a.curriculum?.description || null,
      item_image_url: a.curriculum?.thumbnail_url || null,
      assigned_by_name: a.assigned_by_profile?.full_name || 'Unknown',
      assigned_at: a.assigned_at,
      due_date: a.due_date,
      required: a.required,
      notes: a.notes,
      acknowledged_at: a.acknowledged_at,
    }));

  // Transform course assignments
  const transformedCourses: UserAssignment[] = (courseAssignments as unknown as CourseAssignmentRow[] || [])
    .filter(a => a.course)
    .map(a => ({
      id: a.id,
      type: 'course' as const,
      item_id: a.course_id,
      item_title: a.course?.title || 'Unknown Course',
      item_description: a.course?.description || null,
      item_image_url: a.course?.thumbnail_url || null,
      assigned_by_name: a.assigned_by_profile?.full_name || 'Unknown',
      assigned_at: a.assigned_at,
      due_date: a.due_date,
      required: a.required,
      notes: a.notes,
      acknowledged_at: a.acknowledged_at,
    }));

  // Combine and sort
  const allAssignments = [...transformedCurricula, ...transformedCourses];
  return sortAssignments(allAssignments);
}

/**
 * Fetches incomplete learning assignments for the current user
 * Returns curriculum and course assignments sorted by priority
 */
export function useUserAssignments() {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: ["user_assignments", user?.id],
    queryFn: () => fetchUserAssignments(user!.id),
    enabled: !!user?.id,
  });

  return {
    assignments: query.data || [],
    isLoading: query.isLoading,
    count: query.data?.length || 0,
    error: query.error,
    refetch: query.refetch,
  };
}
